// THE REPLY LOOP, WIRED AT LAST — and wired at MODULE SCOPE, which is the part
// that took two goes.
//
// This app navigates through a MEMORY history, so the URL the server served is
// invisible to the router unless something reads it on purpose. `deeplink.js` is
// that something and it was already running: `main.js` calls `start()` and then
// replays the boot URL with `app.route(location.href)`. What was missing was the
// rule set, which an app has to declare for itself — this one declared none, so
// every link into it landed on the machine's home screen:
//
//   · `/f`, the address printed along the bottom of every page this app makes,
//     whose landing exists for exactly one purpose — "make one back"
//   · `?re=NAME`, which ComposePage has always read and sanitised, in a branch
//     that could not run
//
// WHY THIS IS NOT IN App.vue's <script setup>, WHICH IS WHERE deeplink.js SAYS
// TO PUT IT. `main.js` mounts inside `router.isReady().then(...)` — a microtask
// — and then continues synchronously to `app.route(location.href)`. So the URL
// is emitted on the bus BEFORE any component's `setup()` has run, and the bus
// does not replay: `createBus` in core/util.js keeps no history, so a listener
// that registers one tick late hears nothing at all. Declaring the rules here
// and importing this file from App.vue makes them exist at MODULE EVALUATION,
// which happens on `import App from './App.vue'` at the top of main.js — long
// before the emit.
//
// Both rules degrade to the home screen rather than to a not-found. A link that
// has been shortened, pasted into a message, re-shared and mangled is user
// input, and the machine is the honest answer to one that no longer parses.

import { defineLinks } from '../core/deeplink.js';

/**
 * What a fax header can print, and nothing else.
 *
 * Applied HERE as well as in ComposePage: this value came off the open web and
 * ends up drawn onto an image that a third party will see.
 */
const cleanName = (v) => String(v || '')
  .toUpperCase().replace(/[^A-Z0-9 .&'-]/g, '').trim().slice(0, 20);

defineLinks([
  {
    // Somebody replying to a fax, with the sender's machine named in the link.
    name: 'reply',
    match: ({ params }) => {
      const re = cleanName(params.get('re'));
      return re ? { path: '/tabs/machine/compose', query: { re } } : null;
    },
    href: ({ re }) => (cleanName(re) ? `/app?re=${encodeURIComponent(cleanName(re))}` : null),
  },
  {
    // "Make one back", with nobody named — and the /f landing page's own button.
    // It opens the cover sheet rather than the home screen: someone who has just
    // pressed a button reading "Make one back" has already chosen.
    name: 'compose',
    match: ({ pathname, params }) => (
      params.get('compose') === '1' || /(^|\/)f\/?$/.test(pathname)
        ? { path: '/tabs/machine/compose' }
        : null
    ),
    href: () => '/app?compose=1',
  },
]);
